import React from "react";
import { Navigate } from "react-router-dom";
import permissionsService from "../../services/permissionsService";

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requiredPermissions?: string[];
  /**
   * Restrict to CertainPath staff, matching the back-end voters that scope a
   * surface to the CertainPath tenant rather than to a permission alone.
   *
   * Set this wherever the API enforces the same rule. Without it the nav entry
   * and the route both open, the first fetch returns 403, and the user is left
   * looking at an error on a page they were invited to visit.
   */
  requireCertainPathCompany?: boolean;
}

export default function PermissionGuard({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  requireCertainPathCompany = false,
}: PermissionGuardProps) {
  const { hasRole, hasPermission, hasCertainPathCompany } =
    permissionsService();

  // ROLE_SUPER_ADMIN bypasses all permission checks
  const isSuperAdmin = hasRole("ROLE_SUPER_ADMIN");

  const rolesPass =
    requiredRoles.length === 0 || requiredRoles.some((role) => hasRole(role));

  const permsPass =
    requiredPermissions.length === 0 ||
    requiredPermissions.every((perm) => hasPermission(perm));

  // Super admins administer across tenants, so the company scope does not apply
  // to them — the same carve-out the back-end voters make.
  const companyPass = !requireCertainPathCompany || hasCertainPathCompany();

  const allowed = isSuperAdmin || (rolesPass && permsPass && companyPass);

  if (!allowed) {
    return <Navigate replace to="/403" />;
  }

  return <>{children}</>;
}
